import { dollarsToCents, type BudgetSide } from "./use-budget-editor";

// A signed, whole-dollar delta (e.g. +$1,200 / −$300).
export const signedMoney = (value: number) => {
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  const body = Math.abs(value).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
  return `${sign}${body}`;
};

const money = (value: number) =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

// Delta color: favorable is green. Revenue is favorable when actual > budget;
// cost when actual < budget. No budget, or exactly on budget (delta 0), is neutral.
const deltaClass = (actual: number, budget: number | null, side: BudgetSide) => {
  if (budget === null || actual === budget) return "text-gray-300";
  const favorable = side === "REVENUE" ? actual > budget : actual < budget;
  return favorable ? "text-emerald-600" : "text-rose-600";
};

// The two cells "Actuals vs Planned" mode appends after an actual cell: an
// editable budget input and the actual−budget delta. Returns a fragment of two
// <td>s so it drops straight into a row.
export function BudgetCells({
  actual,
  text,
  onChange,
  side,
}: {
  actual: number;
  text: string;
  onChange: (text: string) => void;
  side: BudgetSide;
}) {
  const cents = dollarsToCents(text);
  const budget = cents === null ? null : cents / 100;
  const delta = budget === null ? null : actual - budget;

  return (
    <>
      <td className="px-1 py-1 text-right bg-amber-50/40 whitespace-nowrap">
        <input
          type="text"
          inputMode="decimal"
          value={text}
          placeholder="—"
          draggable={false}
          onMouseDown={(event) => event.stopPropagation()}
          onDragStart={(event) => event.stopPropagation()}
          onChange={(event) => onChange(event.target.value)}
          className="w-20 text-right tabular-nums bg-white/70 border border-transparent hover:border-gray-200 focus:border-emerald-400 rounded px-1 py-0.5 focus:outline-none"
        />
      </td>
      <td className={`px-2 py-1 text-right tabular-nums whitespace-nowrap text-xs ${deltaClass(actual, budget, side)}`}>
        {delta === null ? "" : signedMoney(delta)}
      </td>
    </>
  );
}

// The read-only counterpart for rolled-up rows (totals, year columns): a computed
// budget figure (sum of the editable cells) and its delta — no input.
export function BudgetSummaryCells({
  actual,
  budget,
  side,
}: {
  actual: number;
  budget: number | null;
  side: BudgetSide;
}) {
  const delta = budget === null ? null : actual - budget;
  return (
    <>
      <td className="px-2 py-1 text-right tabular-nums whitespace-nowrap bg-amber-50/40 text-gray-600">
        {budget === null ? <span className="text-gray-300">—</span> : money(budget)}
      </td>
      <td className={`px-2 py-1 text-right tabular-nums whitespace-nowrap text-xs ${deltaClass(actual, budget, side)}`}>
        {delta === null ? "" : signedMoney(delta)}
      </td>
    </>
  );
}
